import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { FileText, AlertCircle, Search, ExternalLink } from 'lucide-react';
import { apiFetch } from '../lib/supabase';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function BEPLibraryPage() {
  const [projects, setProjects] = useState([]);
  const [entries, setEntries] = useState([]); // flattened BEP rows
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        // Step 1: list the user's projects
        const pResp = await apiFetch(`${API_BASE}/api/projects`);
        if (!pResp.ok) throw new Error(`Projects list failed: HTTP ${pResp.status}`);
        const pJson = await pResp.json();
        const ownedProjects = pJson.projects || [];
        if (cancelled) return;
        setProjects(ownedProjects);

        // Step 2: fan out BEP history requests in parallel
        const histories = await Promise.all(
          ownedProjects.map(async (p) => {
            try {
              const r = await apiFetch(
                `${API_BASE}/api/projects/${p.id}/bep/history?limit=20`
              );
              if (!r.ok) return { project: p, entries: [] };
              const j = await r.json();
              return { project: p, entries: j.entries || [] };
            } catch {
              return { project: p, entries: [] };
            }
          })
        );

        if (cancelled) return;

        // Step 3: flatten into a single list, tagged with project info
        const flat = [];
        histories.forEach(({ project, entries: rows }) => {
          rows.forEach((row) =>
            flat.push({
              ...row,
              project_id: project.id,
              project_name: project.name,
              project_code: project.code,
            })
          );
        });

        // Most-recent first
        flat.sort((a, b) =>
          (b.analyzed_at || '').localeCompare(a.analyzed_at || '')
        );
        setEntries(flat);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load BEP library');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const q = filter.trim().toLowerCase();
  const visible = !q
    ? entries
    : entries.filter(
        (e) =>
          (e.project_name || '').toLowerCase().includes(q) ||
          (e.project_code || '').toLowerCase().includes(q) ||
          (e.file_name || '').toLowerCase().includes(q) ||
          (e.document_version || '').toLowerCase().includes(q) ||
          (e.extracted_by || '').toLowerCase().includes(q)
      );

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="bep-library-page">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground mb-2 flex items-center gap-2">
            <FileText className="h-7 w-7 text-primary" /> BEP Library
          </h1>
          <p className="text-muted-foreground">
            Every Building Execution Plan analysed across your projects, with
            extraction metadata and version history.
          </p>
        </div>

        <Alert className="mb-6" data-testid="bep-library-info">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            BEPs are stored per-project in <code>bep_registry</code>. To upload
            a new BEP, open the project page and use its <em>Upload BEP</em>{' '}
            zone. This view is read-only across your owned projects.
          </AlertDescription>
        </Alert>

        <div className="mb-4 relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by project, file, version, or uploader…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="pl-9"
            data-testid="bep-library-search-input"
          />
        </div>

        {loading && (
          <p
            className="text-muted-foreground py-6"
            data-testid="bep-library-loading"
          >
            Loading BEPs from your projects…
          </p>
        )}

        {error && (
          <Alert variant="destructive" data-testid="bep-library-error">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!loading && !error && projects.length === 0 && (
          <Card data-testid="bep-library-no-projects">
            <CardContent className="py-10 text-center text-muted-foreground">
              You don't own any projects yet. Create one from{' '}
              <Link to="/projects" className="text-primary underline">
                Projects
              </Link>{' '}
              first.
            </CardContent>
          </Card>
        )}

        {!loading && !error && projects.length > 0 && entries.length === 0 && (
          <Card data-testid="bep-library-empty-state">
            <CardContent className="py-10 text-center text-muted-foreground">
              No BEPs have been uploaded across your{' '}
              <strong>{projects.length}</strong> project
              {projects.length === 1 ? '' : 's'} yet. Upload a BEP from any
              project's detail page to populate this library.
            </CardContent>
          </Card>
        )}

        {!loading && !error && visible.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Recent BEP extractions</span>
                <Badge variant="outline">{visible.length}</Badge>
              </CardTitle>
              <CardDescription>
                Showing {visible.length} of {entries.length} extractions across{' '}
                {projects.length} project{projects.length === 1 ? '' : 's'}.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <ul className="divide-y divide-border">
                {visible.map((e) => (
                  <li
                    key={e.id}
                    className="px-4 py-3"
                    data-testid={`bep-library-row-${e.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {e.file_name || 'Unnamed BEP'}
                        </p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          <Link
                            to={`/projects/${e.project_id}`}
                            className="text-primary hover:underline"
                          >
                            {e.project_name}
                          </Link>
                          {e.project_code ? ` · ${e.project_code}` : ''}
                          {e.document_version
                            ? ` · v${e.document_version}`
                            : ''}
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
                          <span>Analysed: {formatDate(e.analyzed_at)}</span>
                          {e.extracted_by && (
                            <span>By: {e.extracted_by}</span>
                          )}
                          {typeof e.processing_time_seconds === 'number' && (
                            <span>
                              {e.processing_time_seconds}s processing
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge
                          variant="outline"
                          className="text-[10px] uppercase tracking-wider"
                        >
                          {e.rules_count || 0} rules
                        </Badge>
                        {!e.has_stored_rules && (
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border bg-yellow-100 text-yellow-800 border-yellow-200">
                            No stored rules
                          </span>
                        )}
                        <Link
                          to={`/projects/${e.project_id}`}
                          className="text-[11px] text-primary hover:underline flex items-center gap-1 mt-1"
                          data-testid={`bep-library-open-project-${e.id}`}
                        >
                          Open project <ExternalLink className="h-3 w-3" />
                        </Link>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
